import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileSignature, Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CustomerPicker } from '@/components/customer-picker';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Alert, Badge, Card, CardContent, CardHeader, CardTitle, Checkbox, Input, Select, Skeleton, Switch, Textarea } from '@/components/ui/primitives';
import { EmptyRow, Pagination, Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { BILLING_LABELS, centsToEuro, ClausesEditor, ContractDocuments, euroToCents } from '@/components/contract-parts';
import type { BillingInterval, ContractClause, ContractDetail, ContractItem, ContractStatus, ContractTemplateItem, DeviceItem, Paginated, Ref, SlaPolicyItem, TicketMeta } from '@/lib/types';
import { formatDate } from '@/lib/utils';
import { Tabs } from '@/components/tickets';
import { TemplatesTab } from './templates-tab';

const onError = (e: unknown) => toast.error(errorMessage(e));

export const CONTRACT_STATUS: Record<ContractStatus, { label: string; variant: 'success' | 'secondary' | 'outline' | 'destructive' | 'warning' }> = {
  DRAFT: { label: 'Entwurf', variant: 'outline' },
  ACTIVE: { label: 'Aktiv', variant: 'success' },
  SUSPENDED: { label: 'Ruhend', variant: 'warning' },
  EXPIRED: { label: 'Abgelaufen', variant: 'secondary' },
  TERMINATED: { label: 'Gekündigt', variant: 'destructive' },
};

export function ContractStatusBadge({ c }: { c: Pick<ContractItem, 'status' | 'effective' | 'endDate'> }) {
  const s = CONTRACT_STATUS[c.status];
  if (c.status === 'ACTIVE' && !c.effective) return <Badge variant="outline">{c.endDate && new Date(c.endDate) < new Date() ? 'Laufzeit beendet' : 'Noch nicht begonnen'}</Badge>;
  return <Badge variant={s.variant}>{s.label}</Badge>;
}

interface Form {
  kind: 'SERVICE' | 'DPA';
  customer: Ref | null;
  title: string;
  type: string;
  status: ContractStatus;
  startDate: string;
  endDate: string;
  autoRenew: boolean;
  renewalMonths: number;
  noticeDays: string;
  services: string;
  areaIds: string[];
  slaPolicyId: string;
  contactId: string;
  ownerId: string;
  deviceIds: string[];
  notes: string;
  templateId: string;
  price: string;
  billingInterval: BillingInterval | '';
  workBilling: 'BILLABLE' | 'INCLUDED' | 'QUOTA';
  quotaHours: string;
  quotaPeriod: 'MONTH' | 'QUARTER' | 'YEAR';
  clauses: ContractClause[];
}

const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = (customer: Ref | null = null, kind: Form['kind'] = 'SERVICE'): Form => ({
  kind,
  customer,
  title: '',
  type: '',
  status: 'ACTIVE',
  startDate: today(),
  endDate: '',
  autoRenew: false,
  renewalMonths: 12,
  noticeDays: '',
  services: '',
  areaIds: [],
  slaPolicyId: '',
  contactId: '',
  ownerId: '',
  deviceIds: [],
  notes: '',
  templateId: '',
  price: '',
  billingInterval: '',
  workBilling: 'BILLABLE',
  quotaHours: '',
  quotaPeriod: 'MONTH',
  clauses: [],
});

/** Vorbelegung aus einer Vorlage (Kunde und Beginn bleiben erhalten). */
function applyTemplate(f: Form, t: ContractTemplateItem): Form {
  let endDate = '';
  if (t.termMonths && f.startDate) {
    const d = new Date(`${f.startDate}T00:00:00Z`);
    d.setUTCMonth(d.getUTCMonth() + t.termMonths);
    d.setUTCDate(d.getUTCDate() - 1);
    endDate = d.toISOString().slice(0, 10);
  }
  return {
    ...f,
    templateId: t.id,
    title: t.title,
    type: t.type,
    services: t.services.join('\n'),
    areaIds: t.areaIds,
    slaPolicyId: t.slaPolicyId ?? '',
    endDate,
    autoRenew: t.autoRenew,
    renewalMonths: t.renewalMonths,
    noticeDays: t.noticeDays?.toString() ?? '',
    price: centsToEuro(t.priceCents),
    billingInterval: t.billingInterval ?? '',
    clauses: t.clauses,
  };
}

const fromDetail = (c: ContractDetail): Form => ({
  kind: c.kind,
  customer: c.customer,
  title: c.title,
  type: c.type,
  status: c.status,
  startDate: c.startDate.slice(0, 10),
  endDate: c.endDate?.slice(0, 10) ?? '',
  autoRenew: c.autoRenew,
  renewalMonths: c.renewalMonths,
  noticeDays: c.noticeDays?.toString() ?? '',
  services: c.services.join('\n'),
  areaIds: c.areas.map((a) => a.id),
  slaPolicyId: c.slaPolicy?.id ?? '',
  contactId: c.contact?.id ?? '',
  ownerId: c.owner?.id ?? '',
  deviceIds: c.devices.map((d) => d.id),
  notes: c.notes ?? '',
  templateId: c.templateId ?? '',
  price: centsToEuro(c.priceCents),
  billingInterval: c.billingInterval ?? '',
  workBilling: c.workBilling,
  quotaHours: c.quotaMinutes ? String(c.quotaMinutes / 60).replace('.', ',') : '',
  quotaPeriod: c.quotaPeriod,
  clauses: c.clauses,
});

/** Anlegen, Ansehen und Bearbeiten eines Vertrags. */
export function ContractDialog({ open, contractId, customer, kind = 'SERVICE', onClose }: { open: boolean; contractId: string | null; customer?: Ref | null; kind?: Form['kind']; onClose: () => void }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const canManage = can('contracts.manage');
  const [form, setForm] = useState<Form>(emptyForm(customer ?? null));
  const [confirmDialog, confirm] = useConfirm();
  const { data: detail } = useQuery({ queryKey: ['contracts', contractId], queryFn: () => api<ContractDetail>(`/contracts/${contractId}`), enabled: open && contractId !== null });
  useEffect(() => {
    if (!open) return;
    if (contractId && detail) setForm(fromDetail(detail));
    if (!contractId) setForm(emptyForm(customer ?? null, kind));
  }, [open, contractId, detail, customer, kind]);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  const { data: meta } = useQuery({ queryKey: ['tickets', 'meta'], queryFn: () => api<TicketMeta>('/tickets/meta'), enabled: open });
  const { data: policies } = useQuery({ queryKey: ['sla-policies'], queryFn: () => api<SlaPolicyItem[]>('/sla-policies'), enabled: open });
  const { data: types } = useQuery({ queryKey: ['contracts', 'types'], queryFn: () => api<{ items: string[] }>('/contracts/types'), enabled: open });
  const { data: templates } = useQuery({ queryKey: ['contract-templates'], queryFn: () => api<{ items: ContractTemplateItem[] }>('/contract-templates'), enabled: open });
  const isDpa = form.kind === 'DPA';
  const kindTemplates = templates?.items.filter((t) => t.active && t.kind === form.kind) ?? [];
  // Neuer AVV: die AVV-Vorlage aus dem Datenschutz-Assistenten direkt vorbelegen
  const dpaTemplate = isDpa && !contractId ? kindTemplates[0] : undefined;
  useEffect(() => {
    if (open && dpaTemplate) setForm((f) => (f.templateId ? f : applyTemplate(f, dpaTemplate)));
  }, [open, dpaTemplate]);
  const custId = form.customer?.id;
  const { data: cust } = useQuery({
    queryKey: ['customers', custId],
    queryFn: () => api<{ contacts: { id: string; firstName: string; lastName: string; email: string | null }[] }>(`/customers/${custId}`),
    enabled: open && Boolean(custId),
  });
  const { data: devices } = useQuery({
    queryKey: ['devices', 'customer', custId],
    queryFn: () => api<Paginated<DeviceItem>>(`/devices?customerId=${custId}&pageSize=100`),
    enabled: open && Boolean(custId),
  });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        kind: form.kind,
        customerId: form.customer!.id,
        title: form.title,
        type: form.type,
        status: form.status,
        startDate: form.startDate,
        endDate: form.endDate || null,
        autoRenew: form.autoRenew,
        renewalMonths: form.renewalMonths,
        noticeDays: form.noticeDays ? Number(form.noticeDays) : null,
        services: form.services.split('\n').map((s) => s.trim()).filter(Boolean),
        areaIds: form.areaIds,
        slaPolicyId: form.slaPolicyId || null,
        contactId: form.contactId || null,
        ownerId: form.ownerId || null,
        deviceIds: form.deviceIds,
        notes: form.notes || null,
        templateId: form.templateId || null,
        priceCents: euroToCents(form.price),
        billingInterval: form.billingInterval || null,
        workBilling: form.workBilling,
        quotaMinutes: form.workBilling === 'QUOTA' ? Math.round(Number(form.quotaHours.replace(',', '.')) * 60) || null : null,
        quotaPeriod: form.quotaPeriod,
        clauses: form.clauses.filter((c) => c.heading.trim() && c.text.trim()),
      };
      return contractId ? api<ContractDetail>(`/contracts/${contractId}`, { method: 'PUT', body }) : api<ContractDetail>('/contracts', { method: 'POST', body });
    },
    onSuccess: (c) => {
      toast.success(contractId ? 'Vertrag gespeichert' : `${c.kind === 'DPA' ? 'AVV' : 'Vertrag'} ${c.number} angelegt`);
      qc.invalidateQueries({ queryKey: ['contracts'] });
      if (contractId) qc.setQueryData(['contracts', contractId], c);
      else onClose();
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: () => api(`/contracts/${contractId}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Entwurf gelöscht');
      qc.invalidateQueries({ queryKey: ['contracts'] });
      onClose();
    },
    onError,
  });
  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const loading = contractId !== null && !detail;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      {confirmDialog}
      <DialogContent wide>
        <form
          className="flex min-h-0 flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>{contractId ? `${isDpa ? 'AVV' : 'Vertrag'} ${detail?.number ?? ''}` : isDpa ? 'Neuer Vertrag zur Auftragsverarbeitung' : 'Neuer Wartungsvertrag'}</DialogTitle>
            <DialogDescription>
              {isDpa
                ? 'Datenarten, TOMs und Unterauftragsverarbeiter werden beim Anlegen aus Administration → Datenschutz übernommen und im Vertrag eingefroren.'
                : 'Freigeschaltete Ticketbereiche gelten im Kundenportal für Bereiche mit Zugriff „Wartungsvertragskunden“, solange der Vertrag aktiv ist und läuft.'}
            </DialogDescription>
          </DialogHeader>
          {loading ? (
            <DialogBody>
              <Skeleton className="h-80" />
            </DialogBody>
          ) : (
            <DialogBody className="grid gap-5">
              <fieldset disabled={!canManage} className="grid gap-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  {!contractId && isDpa && templates && kindTemplates.length === 0 && (
                    <Alert variant="destructive" className="sm:col-span-2">
                      Noch keine AVV-Vorlage vorhanden. Bitte zuerst den{' '}
                      <Link to="/admin/privacy" className="font-medium underline">
                        Datenschutz-Assistenten
                      </Link>{' '}
                      abschließen.
                    </Alert>
                  )}
                  {!contractId && kindTemplates.length > 0 && (
                    <Field label="Vorlage" htmlFor="c-tpl" className="sm:col-span-2" hint={isDpa ? 'Belegt Gegenstand der Verarbeitung und AVV-Klauseln vor.' : 'Belegt Leistungen, Laufzeit, Vergütung, SLA, Bereiche und Vertragsbedingungen vor.'}>
                      <Select
                        id="c-tpl"
                        value={form.templateId}
                        onChange={(e) => {
                          const t = templates?.items.find((x) => x.id === e.target.value);
                          setForm((f) => (t ? applyTemplate(f, t) : { ...f, templateId: '' }));
                        }}
                      >
                        <option value="">– ohne Vorlage –</option>
                        {kindTemplates.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  )}
                  <Field label="Kunde" className="sm:col-span-2">
                    {contractId ? <p className="text-sm font-medium">{form.customer?.name}</p> : <CustomerPicker value={form.customer} onChange={(c) => setForm((f) => ({ ...f, customer: c, contactId: '', deviceIds: [] }))} />}
                  </Field>
                  <Field label="Bezeichnung" htmlFor="c-title">
                    <Input id="c-title" required value={form.title} onChange={(e) => set('title', e.target.value)} placeholder={isDpa ? 'Vertrag zur Auftragsverarbeitung' : 'z. B. IT-Wartung Premium'} />
                  </Field>
                  <Field label="Vertragsart" htmlFor="c-type">
                    <Input id="c-type" required list="contract-types" value={form.type} onChange={(e) => set('type', e.target.value)} placeholder="z. B. IT-Wartung" />
                    <datalist id="contract-types">
                      {[...new Set([...(types?.items ?? []), 'IT-Wartung', 'Druckerservice', 'Hardware-Service', 'Netzwerkbetreuung'])].map((t) => (
                        <option key={t} value={t} />
                      ))}
                    </datalist>
                  </Field>
                  <Field label="Status" htmlFor="c-status">
                    <Select id="c-status" value={form.status} onChange={(e) => set('status', e.target.value as ContractStatus)}>
                      {Object.entries(CONTRACT_STATUS).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Ansprechpartner" htmlFor="c-contact">
                    <Select id="c-contact" value={form.contactId} onChange={(e) => set('contactId', e.target.value)} disabled={!custId}>
                      <option value="">– keiner –</option>
                      {cust?.contacts.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.firstName} {c.lastName}
                          {c.email ? ` (${c.email})` : ''}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Beginn" htmlFor="c-start">
                    <Input id="c-start" type="date" required value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />
                  </Field>
                  <Field label="Ende (leer = unbefristet)" htmlFor="c-end">
                    <Input id="c-end" type="date" value={form.endDate} onChange={(e) => set('endDate', e.target.value)} />
                  </Field>
                  <label className="flex items-center gap-3 rounded-md border p-3 text-sm">
                    <Switch checked={form.autoRenew} onCheckedChange={(v) => set('autoRenew', v)} />
                    Automatisch verlängern um
                    <Input type="number" min={1} max={120} className="w-20" value={form.renewalMonths} onChange={(e) => set('renewalMonths', Number(e.target.value))} aria-label="Monate" disabled={!form.autoRenew} />
                    Monate
                  </label>
                  <Field label="Kündigungsfrist (Tage)" htmlFor="c-notice">
                    <Input id="c-notice" type="number" min={0} value={form.noticeDays} onChange={(e) => set('noticeDays', e.target.value)} />
                  </Field>
                  {!isDpa && (
                  <Field label="Vergütung (netto, €)" htmlFor="c-price" hint="Erscheint im Vertrag; die Abrechnung erfolgt über Powerbird.">
                    <div className="flex gap-2">
                      <Input id="c-price" inputMode="decimal" value={form.price} onChange={(e) => set('price', e.target.value)} placeholder="z. B. 149,00" />
                      <Select value={form.billingInterval} onChange={(e) => set('billingInterval', e.target.value as Form['billingInterval'])} aria-label="Abrechnungsintervall" className="w-44">
                        <option value="">– Intervall –</option>
                        {(Object.keys(BILLING_LABELS) as BillingInterval[]).map((k) => (
                          <option key={k} value={k}>
                            {BILLING_LABELS[k]}
                          </option>
                        ))}
                      </Select>
                    </div>
                  </Field>
                  )}
                  {!isDpa && (
                    <Field label="Arbeitszeit unter diesem Vertrag" htmlFor="c-work" hint="Wird in der Abrechnung automatisch berücksichtigt.">
                      <div className="flex flex-wrap gap-2">
                        <Select id="c-work" value={form.workBilling} onChange={(e) => set('workBilling', e.target.value as Form['workBilling'])} className="w-auto">
                          <option value="BILLABLE">wird berechnet</option>
                          <option value="INCLUDED">inbegriffen</option>
                          <option value="QUOTA">Freistunden, darüber berechnet</option>
                        </Select>
                        {form.workBilling === 'QUOTA' && (
                          <>
                            <Input inputMode="decimal" value={form.quotaHours} onChange={(e) => set('quotaHours', e.target.value)} placeholder="Std." aria-label="Freistunden" className="w-20" />
                            <Select value={form.quotaPeriod} onChange={(e) => set('quotaPeriod', e.target.value as Form['quotaPeriod'])} aria-label="Zeitraum" className="w-auto">
                              <option value="MONTH">je Monat</option>
                              <option value="QUARTER">je Quartal</option>
                              <option value="YEAR">je Jahr</option>
                            </Select>
                          </>
                        )}
                      </div>
                      {detail?.quotaUsage && (
                        <p className="text-xs text-muted-foreground">
                          {detail.quotaUsage.label}: {(detail.quotaUsage.usedMinutes / 60).toFixed(1).replace('.', ',')} von {(detail.quotaUsage.quotaMinutes / 60).toFixed(1).replace('.', ',')} Std. verbraucht
                          {detail.quotaUsage.usedMinutes > detail.quotaUsage.quotaMinutes && ' – Kontingent überschritten'}
                        </p>
                      )}
                    </Field>
                  )}
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  {!isDpa && (
                  <Field label="Reaktions- und Servicezeiten (SLA)" htmlFor="c-sla" hint="Richtlinien unter Ticket-Konfiguration → SLA">
                    <Select id="c-sla" value={form.slaPolicyId} onChange={(e) => set('slaPolicyId', e.target.value)}>
                      <option value="">– Standard des Mandanten –</option>
                      {policies?.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  )}
                  <Field label="Verantwortlich" htmlFor="c-owner">
                    <Select id="c-owner" value={form.ownerId} onChange={(e) => set('ownerId', e.target.value)}>
                      <option value="">– niemand –</option>
                      {meta?.agents.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>

                {!isDpa && (
                <fieldset className="grid gap-2">
                  <legend className="mb-1 text-sm font-medium">Ticketberechtigungen (Bereiche)</legend>
                  <div className="flex flex-wrap gap-3">
                    {meta?.areas.filter((a) => a.active).map((a) => (
                      <label key={a.id} className="flex items-center gap-2 text-sm">
                        <Checkbox checked={form.areaIds.includes(a.id)} onCheckedChange={() => set('areaIds', toggle(form.areaIds, a.id))} />
                        {a.name}
                      </label>
                    ))}
                  </div>
                </fieldset>
                )}

                <Field label={isDpa ? 'Gegenstand der Verarbeitung (eine Leistung pro Zeile)' : 'Enthaltene Leistungen (eine pro Zeile)'} htmlFor="c-services">
                  <Textarea id="c-services" rows={4} value={form.services} onChange={(e) => set('services', e.target.value)} placeholder={'Fernwartung\nPatchmanagement\n4 Vor-Ort-Einsätze pro Jahr'} />
                </Field>

                {!isDpa && (
                <fieldset className="grid gap-2">
                  <legend className="mb-1 text-sm font-medium">Betreute Geräte</legend>
                  {!custId ? (
                    <p className="text-sm text-muted-foreground">Zuerst den Kunden wählen.</p>
                  ) : devices?.items.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Für diesen Kunden sind keine Geräte erfasst.</p>
                  ) : (
                    <div className="grid max-h-48 gap-1.5 overflow-y-auto rounded-md border p-2 sm:grid-cols-2">
                      {devices?.items.map((d) => (
                        <label key={d.id} className="flex items-center gap-2 text-sm">
                          <Checkbox checked={form.deviceIds.includes(d.id)} onCheckedChange={() => set('deviceIds', toggle(form.deviceIds, d.id))} />
                          {d.manufacturer} {d.model}
                          {d.serialNumber && <span className="font-mono text-xs text-muted-foreground">{d.serialNumber}</span>}
                        </label>
                      ))}
                    </div>
                  )}
                </fieldset>
                )}

                <fieldset className="grid gap-2">
                  <legend className="mb-1 text-sm font-medium">Vertragsbedingungen</legend>
                  <ClausesEditor value={form.clauses} onChange={(v) => set('clauses', v)} disabled={!canManage} />
                </fieldset>

                <Field label="Interne Notizen (nicht im Vertrag)" htmlFor="c-notes">
                  <Textarea id="c-notes" rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
                </Field>
              </fieldset>
              {detail && (
                <fieldset className="grid gap-2 rounded-lg border p-4">
                  <legend className="px-1 text-sm font-medium">Vertragsdokument</legend>
                  <ContractDocuments contract={detail} canManage={canManage} dirty={JSON.stringify(fromDetail(detail)) !== JSON.stringify(form)} />
                </fieldset>
              )}
              {detail && (
                <p className="text-xs text-muted-foreground">
                  {detail.openTickets} offene Tickets laufen unter diesem Vertrag ·{' '}
                  <Link to={`/customers/${detail.customer.id}`} className="hover:underline">
                    Kundenakte
                  </Link>
                </p>
              )}
            </DialogBody>
          )}
          <DialogFooter>
            {contractId && canManage && detail?.status === 'DRAFT' && (
              <Button
                type="button"
                variant="ghost"
                className="mr-auto text-destructive hover:text-destructive"
                onClick={async () => (await confirm({ title: 'Entwurf löschen?', confirmLabel: 'Löschen', destructive: true })) && remove.mutate()}
              >
                Löschen
              </Button>
            )}
            <Button type="button" variant="outline" onClick={onClose}>
              {canManage ? 'Abbrechen' : 'Schließen'}
            </Button>
            {canManage && (
              <Button type="submit" loading={save.isPending} disabled={!form.customer || !form.title || !form.type}>
                {contractId ? 'Speichern' : isDpa ? 'AVV anlegen' : 'Vertrag anlegen'}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Verträge eines Kunden (Kundenakte). */
export function CustomerContractsCard({ customer }: { customer: Ref }) {
  const { can } = useAuth();
  const [open, setOpen] = useState<{ id: string | null; kind?: 'SERVICE' | 'DPA' } | null>(null);
  const { data } = useQuery({
    queryKey: ['contracts', 'customer', customer.id],
    queryFn: () => api<Paginated<ContractItem>>(`/contracts?customerId=${customer.id}&pageSize=50`),
    enabled: can('contracts.read'),
  });
  if (!can('contracts.read')) return null;
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm">Verträge</CardTitle>
        {can('contracts.manage') && (
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" onClick={() => setOpen({ id: null, kind: 'DPA' })}>
              <Plus /> AVV
            </Button>
            <Button size="sm" variant="outline" onClick={() => setOpen({ id: null })}>
              <Plus /> Vertrag
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="grid gap-2">
        {data?.items.length === 0 && <p className="text-sm text-muted-foreground">Keine Verträge.</p>}
        {data?.items.map((c) => (
          <button key={c.id} type="button" onClick={() => setOpen({ id: c.id })} className="rounded-md border p-2.5 text-left text-sm transition-colors hover:bg-muted/60">
            <span className="flex flex-wrap items-center gap-2 font-medium">
              {c.title} {c.kind === 'DPA' && <Badge variant="outline">AVV</Badge>} <ContractStatusBadge c={c} />
            </span>
            <span className="block text-muted-foreground">
              {c.number} · {c.type} · {formatDate(c.startDate)} – {c.endDate ? formatDate(c.endDate) : 'unbefristet'}
            </span>
            {c.areas.length > 0 && <span className="block text-xs text-muted-foreground">Bereiche: {c.areas.map((a) => a.name).join(', ')}</span>}
          </button>
        ))}
      </CardContent>
      <ContractDialog open={open !== null} contractId={open?.id ?? null} customer={customer} kind={open?.kind} onClose={() => setOpen(null)} />
    </Card>
  );
}

export function ContractsPage() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [filter, setFilter] = useState<'' | ContractStatus | 'expiring'>('');
  const openId = params.get('id');
  const [creating, setCreating] = useState<'SERVICE' | 'DPA' | null>(params.get('new') === 'dpa' ? 'DPA' : null);
  const [kind, setKind] = useState<'' | 'SERVICE' | 'DPA'>('');
  const [tab, setTab] = useState<'contracts' | 'templates'>('contracts');
  useEffect(() => {
    const t = setTimeout(() => (setDebounced(q), setPage(1)), 250);
    return () => clearTimeout(t);
  }, [q]);
  const query = new URLSearchParams({ page: String(page), pageSize: '25', ...(debounced && { q: debounced }), ...(kind && { kind }), ...(filter === 'expiring' ? { expiringDays: '60' } : filter ? { status: filter } : {}) });
  const { data } = useQuery({ queryKey: ['contracts', 'list', query.toString()], queryFn: () => api<Paginated<ContractItem>>(`/contracts?${query}`) });

  return (
    <div>
      <PageHeader
        title="Verträge"
        description="Wartungsverträge mit Laufzeiten, Leistungen, SLA und Ticketberechtigungen sowie Verträge zur Auftragsverarbeitung (AVV) – jeweils mit PDF."
        actions={
          can('contracts.manage') && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setCreating('DPA')}>
                <Plus /> AVV
              </Button>
              <Button onClick={() => setCreating('SERVICE')}>
                <Plus /> Vertrag
              </Button>
            </div>
          )
        }
      />
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { value: 'contracts', label: 'Verträge' },
          { value: 'templates', label: 'Vorlagen' },
        ]}
      />
      <div className="mt-4">
      {tab === 'templates' ? <TemplatesTab /> : (
      <Card>
        <div className="flex flex-wrap gap-2 border-b p-3">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
            <Input className="pl-8" placeholder="Nummer, Bezeichnung, Kunde" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Suche" />
          </div>
          <Select className="w-auto" value={kind} onChange={(e) => (setKind(e.target.value as typeof kind), setPage(1))} aria-label="Art">
            <option value="">Alle Arten</option>
            <option value="SERVICE">Wartungsverträge</option>
            <option value="DPA">Auftragsverarbeitung (AVV)</option>
          </Select>
          <Select className="w-auto" value={filter} onChange={(e) => (setFilter(e.target.value as typeof filter), setPage(1))} aria-label="Filter">
            <option value="">Alle Verträge</option>
            <option value="ACTIVE">Aktiv</option>
            <option value="expiring">Läuft in 60 Tagen ab</option>
            <option value="DRAFT">Entwürfe</option>
            <option value="EXPIRED">Abgelaufen</option>
            <option value="TERMINATED">Gekündigt</option>
          </Select>
        </div>
        <Table>
          <THead>
            <TR className="hover:bg-transparent">
              <TH>Vertrag</TH>
              <TH>Kunde</TH>
              <TH className="hidden md:table-cell">Laufzeit</TH>
              <TH className="hidden lg:table-cell">SLA / Bereiche</TH>
              <TH>Status</TH>
            </TR>
          </THead>
          <TBody>
            {data?.items.length === 0 && (
              <EmptyRow colSpan={5}>
                <span className="inline-flex items-center gap-2">
                  <FileSignature className="size-4" /> Keine Verträge gefunden.
                </span>
              </EmptyRow>
            )}
            {data?.items.map((c) => (
              <TR key={c.id} className="cursor-pointer" onClick={() => setParams({ id: c.id })}>
                <TD>
                  <p className="font-medium">
                    {c.title} {c.kind === 'DPA' && <Badge variant="outline">AVV</Badge>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {c.number} · {c.type}
                  </p>
                </TD>
                <TD className="text-sm">{c.customer.name}</TD>
                <TD className="hidden whitespace-nowrap text-sm md:table-cell">
                  {formatDate(c.startDate)} – {c.endDate ? formatDate(c.endDate) : 'unbefristet'}
                  {c.autoRenew && <span className="text-xs text-muted-foreground"> · verlängert sich</span>}
                </TD>
                <TD className="hidden text-sm lg:table-cell">
                  {c.kind === 'DPA' ? (
                    <span className="text-muted-foreground">–</span>
                  ) : (
                    <>
                      <p>{c.slaPolicy?.name ?? <span className="text-muted-foreground">Standard</span>}</p>
                      <p className="text-xs text-muted-foreground">{c.areas.map((a) => a.name).join(', ') || 'keine Bereiche'}</p>
                    </>
                  )}
                </TD>
                <TD>
                  <ContractStatusBadge c={c} />
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
        {data && <Pagination page={page} pageSize={25} total={data.total} onPage={setPage} />}
      </Card>
      )}
      </div>
      <ContractDialog
        open={creating !== null || openId !== null}
        contractId={creating ? null : openId}
        kind={creating ?? 'SERVICE'}
        onClose={() => {
          setCreating(null);
          if (openId || params.get('new')) setParams({});
        }}
      />
    </div>
  );
}
